import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const OFFSET = 40;
const VIEWPORT_MARGIN = 16;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #revealFrame = 0;

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    if (this.dataset.viewportClamp === 'true') {
      this.#clampToViewport();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #clampToViewport = () => {
    const parent = this.closest('details');
    if (!(parent instanceof HTMLDetailsElement) || !parent.open) return;

    const viewportWidth = window.innerWidth;
    const maxWidth = Math.max(0, viewportWidth - VIEWPORT_MARGIN * 2);
    const parentRect = parent.getBoundingClientRect();

    this.style.setProperty('top', 'calc(100% + 4px)');
    this.style.setProperty('right', 'auto');
    this.style.setProperty('max-width', `${maxWidth}px`);

    this.style.setProperty('left', '0px');
    const panelWidth = Math.min(this.offsetWidth, maxWidth);
    const maxRight = viewportWidth - VIEWPORT_MARGIN;
    const minLeft = VIEWPORT_MARGIN;

    let nextLeft = 0;
    const panelRight = parentRect.left + panelWidth;

    if (panelRight > maxRight) {
      nextLeft = maxRight - panelWidth - parentRect.left;
    }

    if (parentRect.left + nextLeft < minLeft) {
      nextLeft = minLeft - parentRect.left;
    }

    this.style.setProperty('left', `${nextLeft}px`);
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      this.#closePanel();
      return;
    }

    if (parent instanceof HTMLDetailsElement && parent.open && this.dataset.viewportClamp === 'true') {
      this.#clampToViewport();
      this.#syncOverflow();
    }
  }, 100);

  #cancelReveal = () => {
    if (this.#revealFrame) {
      cancelAnimationFrame(this.#revealFrame);
      this.#revealFrame = 0;
    }
  };

  #resetVisualState = () => {
    this.#cancelReveal();
    this.classList.remove('is-ready', 'is-scrollable');
    this.style.removeProperty('opacity');
    this.style.removeProperty('visibility');
    this.style.removeProperty('pointer-events');
    this.style.removeProperty('transform');
    this.style.removeProperty('display');
    this.style.removeProperty('animation');
  };

  #syncOverflow = () => {
    if (this.dataset.viewportClamp !== 'true') return;

    const parent = this.closest('details');
    if (!(parent instanceof HTMLDetailsElement) || !parent.open) {
      this.classList.remove('is-scrollable');
      return;
    }

    this.classList.remove('is-scrollable');
    const needsScroll = this.scrollHeight > this.clientHeight + 1;
    this.classList.toggle('is-scrollable', needsScroll);
  };

  #onTouchMove = (event) => {
    if (this.dataset.viewportClamp !== 'true') return;
    if (this.classList.contains('is-scrollable')) return;
    event.preventDefault();
  };

  #closePanel = () => {
    const parent = this.closest('details');
    if (!(parent instanceof HTMLDetailsElement) || !parent.open) {
      this.#resetVisualState();
      return;
    }

    this.#resetVisualState();
    parent.open = false;
  };

  #onCloseClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-close-floating-panel]')) return;

    event.preventDefault();
    event.stopPropagation();
    this.#closePanel();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const parent = this.closest('details');
    if (!(parent instanceof HTMLDetailsElement) || !parent.open) return;

    event.preventDefault();
    this.#closePanel();
  };

  #revealPanel = () => {
    this.#cancelReveal();
    this.classList.remove('is-ready');
    this.#clampToViewport();

    this.#revealFrame = requestAnimationFrame(() => {
      this.#revealFrame = requestAnimationFrame(() => {
        this.#revealFrame = 0;
        this.classList.add('is-ready');
        this.#syncOverflow();
      });
    });
  };

  #onToggle = () => {
    const parent = this.closest('details');
    if (!(parent instanceof HTMLDetailsElement)) return;

    if (this.dataset.viewportClamp !== 'true') {
      if (parent.open) requestAnimationFrame(() => this.#updatePosition());
      return;
    }

    this.#cancelReveal();

    if (!parent.open) {
      this.#resetVisualState();
      return;
    }

    this.#revealPanel();
  };

  #onOptionChange = (event) => {
    if (this.dataset.viewportClamp !== 'true') return;
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#closePanel();
  };

  #onFilterUpdate = () => {
    if (this.dataset.viewportClamp !== 'true') return;
    this.#closePanel();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('pointerdown', this.#onCloseClick);
    this.addEventListener('click', this.#onCloseClick);
    this.addEventListener('change', this.#onOptionChange);
    this.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    document.addEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    document.addEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);

    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      parent.addEventListener('toggle', this.#onToggle);
      parent.addEventListener('keydown', this.#onKeyDown);
    }

    requestIdleCallback(() => {
      if (this.dataset.viewportClamp === 'true') {
        if (parent instanceof HTMLDetailsElement && parent.open) {
          this.#revealPanel();
        }
        return;
      }

      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelReveal();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('pointerdown', this.#onCloseClick);
    this.removeEventListener('click', this.#onCloseClick);
    this.removeEventListener('change', this.#onOptionChange);
    this.removeEventListener('touchmove', this.#onTouchMove);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#onFilterUpdate);
    document.removeEventListener(StandardEvents.searchUpdate, this.#onFilterUpdate);

    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      parent.removeEventListener('toggle', this.#onToggle);
      parent.removeEventListener('keydown', this.#onKeyDown);
    }

    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
